
import { defineComponent, computed, onMounted, onUnmounted, ref } from "vue";
import store from "@/store";
import router from "@/router";
import NativeClient, { TitleInfo } from "@/client/native-client";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil, { isValidApiAccessToken } from "@/utils/common-utils";
import MypageFoodListItem from "@/components/mypage/MypageFoodListItem.vue";
import { RecipeFoods, DEFALT_FOOD_ID } from "@/types/pantry";
import TheLoadingMessage from "@/components/common/TheLoadingMessage.vue";
import { MypageShoppingListRegistListItem } from "@/types/mypage";
import { PI_2 } from "pixi.js-legacy";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";

export default defineComponent({
  name: "MypageFavoriteFoodsPage",
  components: {
    MypageFoodListItem,
    CommonDesignDialog
  },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // ログIDの設定
    setLogId(PAGE_ID.MYPAGE_FAVORITE_FOODS);

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "お気に入り食材"));

    // 削除ボタン付き
    NativeClient.sendDebugLog("MypageFavoriteFoodsPage setupstart");
    CommonUtil.scroll({ top: 0, left: 0 });

    const deletingFoodId = ref(DEFALT_FOOD_ID);

    //
    // API実行
    //
    class MypageFavoriteFoodsPageAPIRequest {
      requestType: number;      // 0:なし, 1:お気に入り削除, 2:削除による再取得, 3:買い物リスト登録
      targetId: number;         // リクエスト対象の食品ID
      targetName: string;       // リクエスト対象の食品名
      
      constructor(requestType: number, targetId: number, targetName: string) {
        this.requestType = requestType;
        this.targetId = targetId;
        this.targetName = targetName;
      }
    }
    let currentRequest = new MypageFavoriteFoodsPageAPIRequest(0, 0, "");
    const getFavoriteFoodList = async () => {
      NativeClient.sendDebugLog("MypageFavoriteFoodsPage getFavoriteFoodList");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        currentRequest = new MypageFavoriteFoodsPageAPIRequest(2, 0, "");
        await store.dispatch("mypage/getFavoriteFoodList");
        NativeClient.sendDebugLog("MypageFavoriteFoodsPage getFavoriteFoodList complete");
      }
      store.dispatch("common/stopLoading");
    };
    const deleteFavorite = async (foodId: number) => {
      NativeClient.sendDebugLog("MypageFavoriteFoodsPage deleteFavorite foodId = " + foodId);

      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        currentRequest = new MypageFavoriteFoodsPageAPIRequest(1, foodId, "");
        await store.dispatch("mypage/deleteFavoriteFood", foodId);
        deletingFoodId.value = -1;
        NativeClient.sendDebugLog("MypageFavoriteFoodsPage deleteFolder end");
        currentRequest = new MypageFavoriteFoodsPageAPIRequest(2, 0, "");
        await store.dispatch("mypage/getFavoriteFoodList");
      }
      store.dispatch("common/stopLoading");
    };
    const registShoppingList = async (foodId: number, name: string) => {
      NativeClient.sendDebugLog("MypageFavoriteFoodsPage registShoppingList foodId = " + foodId);
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        currentRequest = new MypageFavoriteFoodsPageAPIRequest(3, foodId, name);
        const foodInfo = new MypageShoppingListRegistListItem(foodId, name);
        await store.dispatch("mypage/registShoppingList", [foodInfo]);
        NativeClient.sendDebugLog("MypageFavoriteFoodsPage registShoppingList end");
      }
      store.dispatch("common/stopLoading");
    };

    // リスト
    class FoodListItem {
      id: number;
      imageUrl: string | null;
      name: string;
      isFavorite: boolean;
      
      constructor(id: number, imageUrl: string | null, name: string, isFavorite: boolean) {
        this.id = id;
        this.imageUrl = imageUrl;
        this.name = name;
        this.isFavorite = isFavorite;
      }
    }
    const isNoElement = computed(() => {
      const list: RecipeFoods | null = store.getters["mypage/getFavoriteFoodList"];
      if (list != null && list.foods.length == 0) {
        return true;
      }
      return false;
    });
    const foodList = computed(() => {
      const list: RecipeFoods | null = store.getters["mypage/getFavoriteFoodList"];
      if (list != null && list.foods.length > 0) {
        NativeClient.sendDebugLog("MypageFavoriteFoodsPage foodList = " + JSON.stringify(list.foods));
        const result: Array<FoodListItem> = [];
        for (const item of list.foods) {
          const element = new FoodListItem(item.foodId, item.image, item.name, true);
          if (deletingFoodId.value == item.foodId) {
            element.isFavorite = false;
          }
          result.push(element);
        }
        NativeClient.sendDebugLog("MypageFavoriteFoodsPage foodList result = " + JSON.stringify(result));
        return result;
      }
      return null;
    });

    // ボタンの処理
    const clickItem = (id: number) => {
      NativeClient.sendDebugLog("MypageFavoriteFoodsPage clickItem foodId = " + id);
      NativeClient.transitionPantryPRFood(`${id}`);
    };
    const clickFavorite = (id: number, isFavorite: boolean) => {
      NativeClient.sendDebugLog("MypageFavoriteFoodsPage clickFavorite foodId = " + id);
      deletingFoodId.value = id;
      deleteFavorite(id);
    };
    const clickShoppingList = (id: number, name: string) => {
      NativeClient.sendDebugLog("MypageFavoriteFoodsPage clickShoppingList foodId = " + id + ", name = " + name);
      registShoppingList(id, name);
    };

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    const okApiErrorHandler = () => {
      deletingFoodId.value = -1;
      closeDialog();
    };
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      closeDialog();
      // switch (currentRequest.requestType) {
      //   case 1:
      //     deleteFavorite(currentRequest.targetId);
      //     break;
      //   case 2:
      //     getFavoriteFoodList();
      //     break;
      //   case 3:
      //     registShoppingList(currentRequest.targetId, currentRequest.targetName);
      //     break;
      //   default:
      //     break;
      // }
    };

    onMounted(() => {
      const logInfo = new BeaconLogInfo(PAGE_ID.MYPAGE_FAVORITE_FOODS, null, []);
      NativeClient.sendViewedLog(logInfo);
    });

    return {
      isNoElement,
      foodList,
      clickItem,
      clickFavorite,
      clickShoppingList,
      typeApiError,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      retryButtonTitle,
      okButtonTitle,
      okApiErrorHandler,
      retryApiErrorHandler
    };
  }
});
